import { AddressesCopy } from "@bluebottlecoffee/design-system/components";
import {
  Address,
  Result,
  ShippingAddress,
} from "@bluebottlecoffee/design-system/components/lib/types";
import { Address as ChordAddressBase } from "@chordcommerce/react-autonomy";

export type ChordAddress = ChordAddressBase & { default?: boolean };

export type StripeBillAddress = {
  name: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
};

export const transformAddress = (chordAddress: ChordAddress): Address => ({
  id: chordAddress.id,
  name: chordAddress.name,
  street: chordAddress.address1,
  extended: chordAddress.address2,
  locality: chordAddress.city,
  district: chordAddress.state?.abbr ?? "",
  postalCode: chordAddress.zipcode,
  countryCode: chordAddress.country.iso,
  default: chordAddress.default,
});

export const transformShippingAddress = (
  chordAddress: ChordAddress,
): ShippingAddress => ({
  id: chordAddress.id,
  name: chordAddress.name,
  firstName: chordAddress.name.split(" ")[0],
  lastName:
    chordAddress.name.substring(chordAddress.name.indexOf(" ") + 1) ?? "",
  street: chordAddress.address1,
  extended: chordAddress.address2,
  locality: chordAddress.city,
  district: chordAddress.state?.abbr ?? "",
  postalCode: chordAddress.zipcode,
  countryCode: chordAddress.country.iso,
  default: chordAddress.default,
});

export const sortAddressByName = (a: Address, b: Address) => {
  const aName = a.name || "";
  const bName = b.name || "";

  if (aName === bName) return 0;

  return aName < bName ? -1 : 1;
};

export const handleSetAsDefault =
  (addressBook: ChordAddress[], modifyAddressBook: any, copy: AddressesCopy) =>
  async (address: Address): Promise<Result> => {
    try {
      let addressBookAddress;
      addressBook.forEach((bookAddress) => {
        if (bookAddress.id === address.id) {
          // eslint-disable-next-line no-param-reassign
          bookAddress.default = true;
          addressBookAddress = bookAddress;
        } else {
          // modify address book entries in-place. Chord handles
          // updating currently default address as no longer default
          //
          // eslint-disable-next-line no-param-reassign
          bookAddress.default = false;
        }
      });

      await modifyAddressBook({
        ...addressBookAddress,
        country: undefined,
        state: undefined,
      });

      return { success: true, message: "" };
    } catch {
      return {
        success: false,
        message: copy.setDefaultFailure,
      };
    }
  };

export const handleUpdateAddress =
  (addressBook: ChordAddress[], modifyAddressBook: any, copy: AddressesCopy) =>
  async (address: Address): Promise<Result> => {
    try {
      const matchingAddresses = addressBook.filter(
        (addr) => addr.id === address.id,
      );

      const transformedAddress: any = {
        name: address.name?.trim(),
        address1: address.street?.trim(),
        address2: address.extended?.trim(),
        city: address.locality?.trim(),
        countryIso: address.countryCode.trim().toUpperCase(), // Chord requires country code to be uppercase
        stateName: address.district?.trim(),
        stateId: "", // explicitly empty as a predefined stateId will override stateName on Chord's end
        zipcode: address.postalCode?.trim(),

        alternativePhone: "",
        company: "",
        countryId: 0,
        default: address.default,
        phone: "",
      };
      const addressBookAddress: ChordAddress = matchingAddresses.length
        ? { ...matchingAddresses[0], ...transformedAddress }
        : { ...transformedAddress };

      await modifyAddressBook({
        ...addressBookAddress,
        country: undefined,
        state: undefined,
      });

      return { success: true, message: copy.editFormUpdateSuccess };
    } catch (error) {
      return {
        success: false,
        message: error.message,
      };
    }
  };

export const handleRemoveAddress =
  (removeAddress: any, copy: AddressesCopy) =>
  async (address: Address): Promise<Result> => {
    try {
      await removeAddress(address.id);

      return { success: true, message: "" };
    } catch {
      return {
        success: false,
        message: copy.confirmRemoveAddressFailure,
      };
    }
  };

export const formatAddressSearchString = (address: Address): string =>
  [
    address.street,
    address.extended,
    address.locality,
    `${address.district} ${address.postalCode}`.trim(),
    address.countryCode,
  ]
    .map((x) => x?.trim())
    .filter((x) => typeof x === "string" && x.length)
    .join(", ");

/**
 * Each component returned from the Places API is assigned at least one `type` tag (see link below).
 * This function inverts those type tags to be used as map keys associated with the address component value
 *
 * @see https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
 */
export const createAddressMap = (
  components: google.maps.GeocoderAddressComponent[],
): Record<string, { longName: string; shortName: string }> =>
  components.reduce((map, component) => {
    component.types.forEach((type) => {
      // skip this because it can be used for multiple types, so it would override previously set values
      if (type === "political") {
        return;
      }

      // eslint-disable-next-line no-param-reassign
      map[type] = {
        longName: component.long_name,
        shortName: component.short_name,
      };
    });
    return map;
  }, {});

/**
 * Take a google-predicted address and transform into an Address object
 */
export const parsePredictedAddress = (
  result: google.maps.places.PlaceResult,
): Address => {
  const { address_components: components } = result;
  const addressMap = createAddressMap(components);
  const filterEmpty = (val: any) =>
    typeof val === "string" && val.trim().length;

  return {
    street: [addressMap?.street_number?.longName, addressMap?.route?.longName]
      .join(" ")
      .trim(),
    extended: [addressMap?.premise?.longName, addressMap?.subpremise?.longName]
      .filter(filterEmpty)
      .join(", "),
    locality: addressMap?.locality?.longName,
    district: addressMap?.administrative_area_level_1?.longName,
    postalCode: addressMap?.postal_code?.longName,
    countryCode: addressMap?.country?.shortName,
    default: false,
  };
};

export const handleSelectAddressPrediction = async (
  prediction: google.maps.places.QueryAutocompletePrediction,
  googlePlacesService: google.maps.places.PlacesService,
  callback: () => void,
): Promise<Address> =>
  new Promise((resolve, reject) => {
    googlePlacesService.getDetails(
      {
        placeId: prediction.place_id,
      },
      (
        result: google.maps.places.PlaceResult,
        status: google.maps.places.PlacesServiceStatus,
      ) => {
        if (status !== "OK") {
          callback();
          reject();
        } else {
          resolve(parsePredictedAddress(result));
          callback();
        }
      },
    );
  });
